import { useMemo } from 'react'
import { useArtistInterests } from './useArtistInterests'
import { useSearchIndex } from './useSearchIndex'

const EMPTY_COUNTS: Map<string, number> = new Map()
const EMPTY_NAMES: Map<string, string[]> = new Map()
const EMPTY_SIZES: Map<string, number> = new Map()

/**
 * The viewer's followed artists on each event's lineup, read off the search
 * index so lineups never have to be fetched per event: `matches` is
 * eventId → count (for ranking), `matchedArtists` eventId → names (for the
 * card subtitle), `lineupSizes` eventId → lineup length (so a subtitle of just
 * the followed ones can say how many more play). `ready` once the answer is final: the index has loaded (or
 * failed — then the maps stay empty), or the viewer follows no artists at all.
 */
export function useEventArtistMatches(userId: string | null) {
  const { interestedArtistIds, loading: interestsLoading } = useArtistInterests(userId)
  const { index, loading: indexLoading } = useSearchIndex()

  const { matches, matchedArtists, lineupSizes } = useMemo(() => {
    if (!index || interestedArtistIds.size === 0) return { matches: EMPTY_COUNTS, matchedArtists: EMPTY_NAMES, lineupSizes: EMPTY_SIZES }
    const names = new Map<string, string[]>()
    const sizes = new Map<string, number>()
    for (const artist of index.artists) {
      const followed = interestedArtistIds.has(artist.id)
      for (const idx of new Set(artist.eventIdx)) {
        const eventId = index.events[idx]?.id
        if (!eventId) continue
        sizes.set(eventId, (sizes.get(eventId) ?? 0) + 1)
        if (!followed) continue
        const list = names.get(eventId)
        if (list) list.push(artist.name)
        else names.set(eventId, [artist.name])
      }
    }
    const counts = new Map([...names].map(([id, list]) => [id, list.length]))
    return { matches: counts, matchedArtists: names, lineupSizes: sizes }
  }, [index, interestedArtistIds])

  const followsArtists = interestedArtistIds.size > 0
  const ready = !userId || (!interestsLoading && (!followsArtists || !indexLoading))
  return { matches, matchedArtists, lineupSizes, followsArtists, ready }
}
